import {EventEmitter, signal} from "@angular/core"
import {deepCopy} from "@cm/utils"
import {
    OperatorCanvasToolboxType,
    OperatorFlags,
    OperatorPanelComponentType,
    OperatorProcessingHints,
} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator"
import {OperatorBase} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-base"
import {OperatorCallback} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-callback"
import {RotatePanelComponent} from "@app/textures/texture-editor/operator-stack/operators/rotate/panel/rotate-panel.component"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {ImageOpNodeOperatorRotate} from "@app/textures/texture-editor/operator-stack/operators/rotate/node/node"
import {AnyImageOpResultNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"

export class OperatorRotate extends OperatorBase<TextureEditNodes.OperatorRotate> {
    // OperatorBase
    override readonly flags = new Set<OperatorFlags>(["apply-to-all-texture-types"]) // rotation is not preserving texture size, so it must be applied to all texture types

    readonly angleInDegreesChanged = new EventEmitter<number>()

    readonly panelComponentType: OperatorPanelComponentType = RotatePanelComponent
    readonly canvasToolbox: OperatorCanvasToolboxType = null

    readonly type = "operator-rotate" as const

    constructor(callback: OperatorCallback, node: TextureEditNodes.OperatorRotate | null) {
        super(
            callback,
            deepCopy(node) ?? {
                type: "operator-rotate",
                enabled: true,
                angleInDegrees: 0,
            },
        )
    }

    // OperatorBase
    override dispose(): void {
        super.dispose()
    }

    // OperatorBase
    async clone() {
        const clonedOperator = new OperatorRotate(this.callback, deepCopy(this.node))
        await clonedOperator.init()
        return clonedOperator
    }

    // OperatorBase
    protected override async createImageOpGraph(
        sourceImage: AnyImageOpResultNode,
        hints: OperatorProcessingHints,
        imageOpNodeBag: ImageOpNodeBag,
    ): Promise<AnyImageOpResultNode> {
        return imageOpNodeBag.getNode("rotate", ImageOpNodeOperatorRotate, {
            sourceImage,
            angleInDegrees: this.node.angleInDegrees,
            textureType: hints.textureType,
        })
    }

    private readonly _$angleInDegrees = signal(this.node.angleInDegrees)
    readonly $angleInDegrees = this._$angleInDegrees.asReadonly()

    setAngleInDegrees(value: number): void {
        if (this.node.angleInDegrees === value) {
            return
        }
        this.node.angleInDegrees = value
        this._$angleInDegrees.set(value)
        this.markEdited()
        this.requestEval()
        this.angleInDegreesChanged.emit(value)
    }
}
